import React, { useState, useRef, useEffect } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import {
    Box, TextField, Select, MenuItem, InputAdornment,
    Typography, Divider, IconButton
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { BRAND_COLORS } from "../constants/taskStatuses";

/**
 * Search bar with column-filter chip selector.
 * Layout: [ All ▼ | <search input> ]
 *
 * @param {{
 *   onSearch: (value: string, field: string) => void,
 *   placeholder?: string,
 *   debounceMs?: number
 * }} props
 */

const SEARCH_FIELDS = [
    { value: "all",           label: "All" },
    { value: "applicationNo", label: "Application Number" },
    { value: "activityName",  label: "Activity Name" },
    { value: "docketNo",      label: "Docket Number" },
    { value: "taskStatus",    label: "Task Status" },
    { value: "oaMailDate",    label: "OA Mail Date" },
    { value: "packageStatus", label: "Package Status" }
];

const PACKAGE_STATUS_OPTIONS = [
    { value: "",        label: "Select Status..." },
    { value: "Pass",    label: "Pass" },
    { value: "Failed",  label: "Failed" },
    { value: "Pending", label: "Pending" }
];

const TASK_STATUS_OPTIONS = [
    { value: "",               label: "Select Status..." },
    { value: "To Do",          label: "To Do" },
    { value: "In Progress",    label: "In Progress" },
    { value: "Assign QA",      label: "Assign QA" },
    { value: "QC Pending",     label: "QC Pending" },
    { value: "Under Review",   label: "Under Review" },
    { value: "Completed",      label: "Completed" }
];

const SUPPORT_TYPE_OPTIONS = [
    { value: "",               label: "Select Support Type..." },
    { value: "Proofreading",   label: "Proofreading" },
    { value: "PTA",            label: "PTA" },
    { value: "CoC",            label: "CoC" }
];

// Fields whose value comes from a fixed set rather than free text. The grid
// cells offer exactly these options (PGSTaskList) and the API validates against
// them (collab-apis validators/workflow.validators.js), so a typed value that is
// not one of these can only ever return an empty result. Add a field here to
// give it a dropdown; no new branch is needed below.
const FIXED_VALUE_OPTIONS = {
    complexity: [
        { value: "Simple", label: "Simple" },
        { value: "Complex", label: "Complex" }
    ],
    errorNoError: [
        { value: "Error", label: "Error" },
        { value: "No Error", label: "No Error" }
    ]
};

const ACTIVITY_OPTIONS = [
    { value: "",                          label: "Select Activity..." },
    { value: "FP & Claims",               label: "FP & Claims" },
    { value: "FP, Claims & Drawings",     label: "FP, Claims & Drawings" },
    { value: "Entire",                    label: "Entire" },
    { value: "PTA",                       label: "PTA" },
    { value: "CoC",                       label: "CoC" }
];

const OASearchBar = ({ onSearch, placeholder, debounceMs = 300, fields, clients = [], statusOptions }) => {
    const resolvedFields = fields || SEARCH_FIELDS;
    const [field, setField] = useState("all");
    const [value, setValue] = useState("");
    const timerRef = useRef(null);
    const onSearchRef = useRef(onSearch);
    useEffect(() => {
        onSearchRef.current = onSearch;
    }, [onSearch]);

    // Sync selected field when custom fields prop changes (e.g. page transition or tab change)
    useEffect(() => {
        if (fields && fields.length > 0) {
            const exists = fields.some(f => f.value === field);
            if (!exists) {
                const fallbackField = fields[0].value;
                setField(fallbackField);
                setValue("");
                onSearchRef.current?.("", fallbackField);
            }
        } else if (field !== "all") {
            setField("all");
            setValue("");
            onSearchRef.current?.("", "all");
        }
    }, [fields, field]);

    const selectedLabel = resolvedFields.find(f => f.value === field)?.label || "All";

    const handleFieldChange = (newField) => {
        setField(newField);
        setValue("");
        onSearch?.("", newField);
    };

    const handleValueChange = (e) => {
        const v = e.target.value;
        setValue(v);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            onSearch?.(v, field);
        }, debounceMs);
    };

    const handleStatusDropdownChange = (v) => {
        setValue(v);
        onSearch?.(v, field);
    };

    const handleClear = () => {
        setValue("");
        if (timerRef.current) clearTimeout(timerRef.current);
        onSearch?.("", field);
    };

    // Cleanup debounce on unmount
    useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

    const resolvedPlaceholder = placeholder
        || (field === "all"
            ? "Search across all fields..."
            : `Search by ${selectedLabel}...`);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                border: `1px solid #E2E8F0`,
                borderRadius: "50px", // Pill shape matched to Figma
                backgroundColor: "#fff",
                height: 36,
                overflow: "hidden",
                width: 500,
                px: 1,
                "&:hover": { borderColor: "#CBD5E1" },
                "&:focus-within": { borderColor: BRAND_COLORS.PRIMARY, boxShadow: `0 0 0 2px ${BRAND_COLORS.PRIMARY}22` }
            }}
            data-testid="oa-search-bar"
        >
            {/* Column selector */}
            <Select
                value={field}
                onChange={(e) => handleFieldChange(e.target.value)}
                variant="standard"
                disableUnderline
                autoWidth
                sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#64748B",
                    pl: 1.5,
                    pr: 0.5,
                    "& .MuiSelect-select": { 
                        py: 0, 
                        pr: "25px !important",
                        backgroundColor: 'transparent !important' 
                    },
                    "& .MuiSvgIcon-root": { fontSize: 16, color: "#94A3B8", right: 4 },
                    "&.Mui-focused .MuiSelect-select": { backgroundColor: 'transparent !important' }
                }}
                data-testid="oa-search-field-selector"
            >
                {resolvedFields.map(f => (
                    <MenuItem key={f.value} value={f.value} sx={{ fontSize: "12px" }}>
                        {f.label}
                    </MenuItem>
                ))}
            </Select>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1, borderColor: "#E2E8F0" }} />

            {/* Search input / Dropdown / Date Picker */}
            {(field === "routedClient" || field === "clientName") ? (
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    <Select
                        value={value}
                        onChange={(e) => handleStatusDropdownChange(e.target.value)}
                        variant="standard"
                        displayEmpty
                        fullWidth
                        disableUnderline
                        sx={{
                            fontSize: "13px",
                            color: "#1E293B",
                            pl: 1,
                            "& .MuiSelect-select": { py: 0, backgroundColor: 'transparent !important' },
                            "& .MuiSvgIcon-root": { fontSize: 18, color: "#94A3B8" }
                        }}
                        data-testid="oa-client-search-dropdown"
                    >
                        <MenuItem value="" sx={{ fontSize: "13px" }}>
                            Select Client...
                        </MenuItem>
                        {clients.map(c => {
                            const val = c.tenantCode || c.code || "";
                            const label = c.tenantName 
                                ? `${c.tenantName} (${c.tenantCode || c.code})` 
                                : (c.clientName || c.name || c.tenantCode || c.code || "");
                            return (
                                <MenuItem key={val} value={val} sx={{ fontSize: "13px" }}>
                                    {label}
                                </MenuItem>
                            );
                        })}
                    </Select>
                    {value && (
                        <IconButton
                            size="small"
                            onClick={handleClear}
                            sx={{ p: 0.5, mr: 0.5 }}
                        >
                            <ClearIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
                        </IconButton>
                    )}
                </Box>
            ) : field === "taskStatus" ? (
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    <Select
                        value={value}
                        onChange={(e) => handleStatusDropdownChange(e.target.value)}
                        variant="standard"
                        displayEmpty
                        fullWidth
                        disableUnderline
                        sx={{
                            fontSize: "13px",
                            color: "#1E293B",
                            pl: 1,
                            "& .MuiSelect-select": { py: 0, backgroundColor: 'transparent !important' },
                            "& .MuiSvgIcon-root": { fontSize: 18, color: "#94A3B8" }
                        }}
                        data-testid="oa-status-search-dropdown"
                    >
                        {(statusOptions || TASK_STATUS_OPTIONS).map(opt => (
                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "13px" }}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {value && (
                        <IconButton
                            size="small"
                            onClick={handleClear}
                            sx={{ p: 0.5, mr: 0.5 }}
                        >
                            <ClearIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
                        </IconButton>
                    )}
                </Box>
            ) : field === "supportType" ? (
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    <Select
                        value={value}
                        onChange={(e) => handleStatusDropdownChange(e.target.value)}
                        variant="standard"
                        displayEmpty
                        fullWidth
                        disableUnderline
                        sx={{
                            fontSize: "13px",
                            color: "#1E293B",
                            pl: 1,
                            "& .MuiSelect-select": { py: 0, backgroundColor: 'transparent !important' },
                            "& .MuiSvgIcon-root": { fontSize: 18, color: "#94A3B8" }
                        }}
                        data-testid="oa-support-type-search-dropdown"
                    >
                        {SUPPORT_TYPE_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "13px" }}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {value && (
                        <IconButton
                            size="small"
                            onClick={handleClear}
                            sx={{ p: 0.5, mr: 0.5 }}
                        >
                            <ClearIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
                        </IconButton>
                    )}
                </Box>
            ) : field === "activityName" ? (
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    <Select
                        value={value}
                        onChange={(e) => handleStatusDropdownChange(e.target.value)}
                        variant="standard"
                        displayEmpty
                        fullWidth
                        disableUnderline
                        sx={{
                            fontSize: "13px",
                            color: "#1E293B",
                            pl: 1,
                            "& .MuiSelect-select": { py: 0, backgroundColor: 'transparent !important' },
                            "& .MuiSvgIcon-root": { fontSize: 18, color: "#94A3B8" }
                        }}
                        data-testid="oa-activity-search-dropdown"
                    >
                        {ACTIVITY_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "13px" }}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {value && (
                        <IconButton
                            size="small"
                            onClick={handleClear}
                            sx={{ p: 0.5, mr: 0.5 }}
                        >
                            <ClearIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
                        </IconButton>
                    )}
                </Box>
            ) : field === "packageStatus" ? (
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    <Select
                        value={value}
                        onChange={(e) => handleStatusDropdownChange(e.target.value)}
                        variant="standard"
                        displayEmpty
                        fullWidth
                        disableUnderline
                        sx={{
                            fontSize: "13px",
                            color: "#1E293B",
                            pl: 1,
                            "& .MuiSelect-select": { py: 0, backgroundColor: 'transparent !important' },
                            "& .MuiSvgIcon-root": { fontSize: 18, color: "#94A3B8" }
                        }}
                        data-testid="oa-package-status-search-dropdown"
                    >
                        {PACKAGE_STATUS_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "13px" }}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {value && (
                        <IconButton
                            size="small"
                            onClick={handleClear}
                            sx={{ p: 0.5, mr: 0.5 }}
                        >
                            <ClearIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
                        </IconButton>
                    )}
                </Box>
            ) : FIXED_VALUE_OPTIONS[field] ? (
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    <Select
                        value={value}
                        onChange={(e) => handleStatusDropdownChange(e.target.value)}
                        variant="standard"
                        displayEmpty
                        fullWidth
                        disableUnderline
                        sx={{
                            fontSize: "13px",
                            color: "#1E293B",
                            pl: 1,
                            "& .MuiSelect-select": { py: 0, backgroundColor: 'transparent !important' },
                            "& .MuiSvgIcon-root": { fontSize: 18, color: "#94A3B8" }
                        }}
                        data-testid="oa-fixed-value-search-dropdown"
                    >
                        <MenuItem value="" sx={{ fontSize: "13px" }}>
                            {`Select ${resolvedFields.find(f => f.value === field)?.label || "value"}...`}
                        </MenuItem>
                        {FIXED_VALUE_OPTIONS[field].map(opt => (
                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "13px" }}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {value && (
                        <IconButton
                            size="small"
                            onClick={handleClear}
                            sx={{ p: 0.5, mr: 0.5 }}
                        >
                            <ClearIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
                        </IconButton>
                    )}
                </Box>
            ) : (field === "oaMailDate" || field === "requestedDate" || field === "deadlineDate") ? (
                <TextField
                    type="date"
                    variant="standard"
                    fullWidth
                    value={value}
                    onChange={handleValueChange}
                    InputProps={{
                        disableUnderline: true,
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: "#94A3B8", ml: 0.5 }} />
                            </InputAdornment>
                        ),
                        endAdornment: value && (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={handleClear}
                                    sx={{ p: 0.5, mr: 0.5 }}
                                >
                                    <ClearIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    sx={{
                        "& input": { fontSize: "13px", color: "#1E293B", py: 0, pl: 1 },
                        "& .MuiInputAdornment-root": { mr: 1 }
                    }}
                    data-testid="oa-date-search-input"
                />
            ) : (
                <TextField
                    variant="standard"
                    fullWidth
                    value={value}
                    onChange={handleValueChange}
                    placeholder={resolvedPlaceholder}
                    InputProps={{
                        disableUnderline: true,
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: "#94A3B8", ml: 0.5 }} />
                            </InputAdornment>
                        ),
                        endAdornment: value && (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={handleClear}
                                    sx={{ p: 0.5, mr: 0.5 }}
                                >
                                    <ClearIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    sx={{
                        "& input": { fontSize: "13px", color: "#1E293B", py: 0 },
                        "& input::placeholder": { color: "#94A3B8", opacity: 1 }
                    }}
                    data-testid="oa-search-input"
                />
            )}
        </Box>
    );
};

export default OASearchBar;
